import React, { useCallback, useEffect, useState } from 'react';
import { Unlock, Lock, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (s) => {
  if (!s || s <= 0) return 'closed';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = Math.floor(s % 60);
  if (h >= 1) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m ${String(ss).padStart(2, '0')}s`;
};

const PRESETS = [1, 6, 12, 24, 48];

/** Open or close the site-wide free-access window (paywall down, accounts
 *  still required) without a redeploy. */
const FreeAccessCard = ({ token }) => {
  const [state, setState] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [hours, setHours] = useState(12);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/free-access`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Could not load window');
      setState(data);
      setSecondsLeft(data.seconds_remaining || 0);
    } catch (e) { setErr(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const t = setInterval(() => setSecondsLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/admin/free-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ hours: Number(hours) }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Could not open window');
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const close = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/admin/free-access`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Could not close window');
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const isOpen = !!state?.active && secondsLeft > 0;

  return (
    <div
      data-testid="free-access-card"
      className={`border rounded-lg p-4 mb-4 ${isOpen ? 'border-amber-600/70 bg-amber-950/20' : 'border-zinc-800 bg-zinc-900/40'}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {isOpen
            ? <Unlock className="h-5 w-5 shrink-0 mt-0.5 text-amber-400" />
            : <Lock className="h-5 w-5 shrink-0 mt-0.5 text-zinc-500" />}
          <div className="min-w-0 flex-1">
            <p className="text-white font-black text-sm">Free-access window · paywall down</p>
            <p className="text-zinc-400 text-xs leading-relaxed mt-0.5">
              Opens every story, the Rumour File, the Gangland Map and the videos to anyone with an
              account. Readers still need to sign up (free) &mdash; anonymous visitors stay locked.
              Readers see the countdown pop-up while it's open, and the paywall re-arms by itself.
            </p>
            <p
              className={`text-[10px] font-black uppercase tracking-[0.25em] mt-2 ${isOpen ? 'text-amber-300' : 'text-zinc-500'}`}
              data-testid="free-access-state"
            >
              {isOpen
                ? `Open · locks back up in ${fmt(secondsLeft)}`
                : 'Closed · subscribers only'}
            </p>
            {isOpen && state?.ends_at && (
              <p className="text-zinc-500 text-[10px] mt-1" data-testid="free-access-ends-at">
                Ends {new Date(state.ends_at).toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' })} (Melbourne)
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            data-testid="free-access-hours"
            className="h-10 bg-zinc-950 border border-zinc-800 rounded px-2 text-sm text-white outline-none focus:border-amber-500"
          >
            {PRESETS.map((h) => (
              <option key={h} value={h}>{h}h</option>
            ))}
          </select>
          <button
            type="button"
            onClick={open}
            disabled={busy}
            data-testid="free-access-open"
            className="inline-flex items-center h-10 px-4 rounded bg-amber-600 hover:bg-amber-500 text-white font-black text-xs uppercase tracking-[0.25em] disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isOpen ? `Reset ${hours}h` : `Open ${hours}h`}
          </button>
          <button
            type="button"
            onClick={close}
            disabled={busy || !isOpen}
            data-testid="free-access-close-now"
            className="inline-flex items-center h-10 px-4 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white font-black text-xs uppercase tracking-[0.25em] disabled:opacity-40"
          >
            Lock now
          </button>
        </div>
      </div>

      {err && <p className="text-red-400 text-xs mt-3" data-testid="free-access-error">{err}</p>}
    </div>
  );
};

export default FreeAccessCard;
